// bankAdapterLinks — keep the wizard's per-bank adapter maps current.
//
// `adapterIdByBank` and `adapterProtocolByBank` are how every step after Banks
// learns which adapter (and so which protocol) a bank uses: the bank record
// carries only `adapter_id`, never a nested adapter. Lifts & Floors keys its
// OPC Lift Path / OPC Group Name inputs off the protocol, Notifications and
// Actions their message scoping, BACnet its bank picker.
//
// The resume hydrator in WizardContext fills both maps from saved data, but
// nothing wrote them for a bank created or re-pointed during the current
// session — so an OPC UA bank made in a fresh wizard showed no OPC inputs on
// Lifts & Floors until the operator reloaded through Drafts -> Resume. The
// Banks step now records every bank it creates, saves, loads or restores
// through this one function, so it and the hydrator cannot disagree about
// what the maps hold.

import type { WizardState } from "../WizardContext";

export type BankAdapterMaps = Pick<WizardState, "adapterIdByBank" | "adapterProtocolByBank">;

export interface BankAdapterLink {
  bankId: string;
  /** The adapter the bank references; `""` when it references none. */
  adapterId: string;
  /** Uppercase AdapterProtocol name of that adapter; `""` when unresolved. */
  protocol: string;
}

/**
 * Return `maps` with each link applied. A bank with no adapter is removed from
 * both maps. An unresolved protocol (`""`) removes the bank's protocol entry
 * rather than keeping a stale one: the old protocol belonged to whatever
 * adapter the bank pointed at before, and consumers already treat an absent
 * entry as "unknown" and fall back to reading the adapter themselves.
 */
export function withBankAdapterLinks(
  maps: BankAdapterMaps,
  links: readonly BankAdapterLink[],
): BankAdapterMaps {
  const adapterIdByBank = { ...maps.adapterIdByBank };
  const adapterProtocolByBank = { ...maps.adapterProtocolByBank };
  links.forEach(({ bankId, adapterId, protocol }) => {
    if (!bankId) return;
    if (adapterId) {
      adapterIdByBank[bankId] = adapterId;
    } else {
      delete adapterIdByBank[bankId];
    }
    if (adapterId && protocol) {
      adapterProtocolByBank[bankId] = protocol.toUpperCase();
    } else {
      delete adapterProtocolByBank[bankId];
    }
  });
  return { adapterIdByBank, adapterProtocolByBank };
}

/**
 * Return `maps` with every bank that references `adapterId` given `protocol`.
 * Used when an adapter's protocol is edited on the Adapters step, so banks
 * already bound to it follow without a reload.
 */
export function withAdapterProtocol(
  maps: BankAdapterMaps,
  adapterId: string,
  protocol: string,
): BankAdapterMaps {
  const links = Object.entries(maps.adapterIdByBank)
    .filter(([, id]) => id === adapterId)
    .map(([bankId]) => ({ bankId, adapterId, protocol }));
  return links.length === 0 ? maps : withBankAdapterLinks(maps, links);
}
